import {useDateTimeFormatter, useFormatCurrency, useFormatNumber} from 'platform/locale';

import i18n from '@dms/i18n';

import {parseDate} from 'shared/web';

import type {
  AuctionMoney,
  AuctionTraderNetwork,
  DateTimeString,
} from '../../model/auctionView.types';

const NETWORK_LABEL: Record<AuctionTraderNetwork, string> = {
  fastback: 'entity.auction.settings.fastback',
  local: 'entity.auction.settings.localTraders',
};

export function useAuctionFormat() {
  const formatCurrency = useFormatCurrency();
  const formatDateTime = useDateTimeFormatter();
  const formatNumber = useFormatNumber();

  return {
    money: (money: AuctionMoney) => {
      const amount = Number(money.amount);
      // The locale data uses a normal space as the thousands separator, so a price can break over two lines.
      const formatted = formatCurrency(amount, money.currency, Number.isInteger(amount) ? 0 : 2);
      return formatted?.replace(/ /g, '\u00a0') ?? null;
    },
    // Rounds down, so a 2.96 rating below the 3-star limit does not show as "3.0". The scale is 0–5.
    rating: (rating: number) =>
      (Math.floor(Math.min(Math.max(rating, 0), 5) * 10 + 1e-9) / 10).toFixed(1),
    // i18next picks the plural form from the number, then the locale number replaces the raw digits.
    count: (key: string, count: number) =>
      i18n.t(key, {count}).replace(String(count), formatNumber(count) ?? String(count)),
    date: (date: DateTimeString) => formatDateTime('dateShort', parseDate(date)),
    dateTime: (date: DateTimeString) => formatDateTime('dateTimeShort', parseDate(date)),
    network: (network: AuctionTraderNetwork | null) =>
      network === null ? null : i18n.t(NETWORK_LABEL[network]),
  };
}
